import React, { useCallback, useEffect, useState } from 'react';
import { format, subDays } from 'date-fns';
import * as ReactDOMClient from 'react-dom/client';
import { Controller, useForm } from 'react-hook-form';
// @ts-ignore
import Modal from 'bootstrap/js/dist/modal';
import MVMIcon from '../Common/MVMIcon';
import Datepicker from '../Common/Datepicker';
import { DATE_PICKER_FORMAT } from '@/common/constants';
import ImageUpload from '../Common/ImageUpload';
import { DistanceService } from '@/common/api/Services/Distance';
import Spinner from '../Common/Spinner';
import MissionItem from './Components/MissionItem';
import PostDistanceModal from './Components/PostDistanceModal';
const today = new Date();
const todayFormatted = format(today, DATE_PICKER_FORMAT);
const endPickerDate = format(subDays(today, 90), DATE_PICKER_FORMAT);
const MIN_STEPS_TO_POST = 150;
const MIN_DISTANCE_TO_POST = 0.01;
const PostDistance = () => {
    // variables
    //@ts-ignore
    const postDistanceData = window.postDistanceData;
    let exerciseTypes = Object.values(postDistanceData.exerciseTypes).map((value) => ({
        alias: value.alias,
        name: value.name,
    }));
    exerciseTypes.unshift({ alias: '', name: 'Select Activity' });
    const { register, handleSubmit, control, formState: { errors }, watch, setValue, } = useForm();
    const exerciseType = watch('exerciseType', postDistanceData.initialExercise);
    // state
    const [missions, setMissions] = useState();
    const [errorMessages, setErrorMessages] = useState([]);
    const [pageStatus, setPageStatus] = useState('loading');
    const [modalData, setModalData] = useState();
    const [isPosting, setIsPosting] = useState(false);
    const [modal, setModal] = useState();
    // functions
    const { getDistanceMissions, postDistance } = DistanceService;
    const isSteps = () => {
        return exerciseType === 'steps';
    };
    const getDefaultCheckedMissions = (missions) => {
        return missions.reduce((checkedMissions, mission) => {
            if (mission.isInitialChecked) {
                return [...checkedMissions, mission.id];
            }
            return checkedMissions;
        }, []);
    };
    const getMinDistanceMessage = () => {
        if (isSteps()) {
            return `Your distance is too low, you must have over ${MIN_STEPS_TO_POST} steps`;
        }
        return `Your distance is too low, you must have over ${MIN_DISTANCE_TO_POST} ${postDistanceData.distanceUnit}`;
    };
    const OnConfirmSubmit = (values) => {
        let errors = [];
        if (values.missionsToPost.length <= 0) {
            errors.push('Please select a mission from the list before posting a distance');
        }
        if (errors.length > 0) {
            setErrorMessages(errors);
            return;
        }
        const travelled = +values.travelled;
        if (travelled > 200 && !isSteps()) {
            const resultcf = confirm("That's a lot of " +
                postDistanceData.distanceUnit +
                '! \nAre you sure you want to enter ' +
                travelled +
                ' ' +
                postDistanceData.distanceUnit +
                '?');
            if (!resultcf) {
                return;
            }
        }
        setModalData(values);
        const modalElement = document.getElementById('modal-activity-post');
        if (modalElement === null) {
            return;
        }
        const modalOnLoad = new Modal(modalElement);
        setModal(modalOnLoad);
        modalOnLoad.show();
    };
    const onSubmit = async (values) => {
        if (isPosting)
            return;
        setIsPosting(true);
        try {
            await postDistance({
                ...values,
                filePath: values.filePath.replace('public/share/uploads/tmp', ''),
                distanceUnit: postDistanceData.distanceUnit.toLowerCase(),
            });
            location.reload();
        }
        catch (error) {
            if (error?.message) {
                setErrorMessages([error.message]);
            }
            setIsPosting(false);
            //@ts-ignore
            modal?.hide();
        }
    };
    const onUpdateMissions = async (alias) => {
        setPageStatus('loading');
        // if Select Activity is selected replace value with "other"
        const validAlias = alias === '' ? 'other' : alias;
        try {
            const newMissions = await getDistanceMissions(validAlias);
            newMissions.sort((m) => (m.locked ? 1 : -1));
            newMissions.sort((m) => (m.isInitialChecked ? -1 : 1));
            setValue('missionsToPost', getDefaultCheckedMissions(newMissions));
            setPageStatus('success');
            setErrorMessages([]);
            setMissions(newMissions);
        }
        catch (e) {
            setPageStatus('error');
            setErrorMessages(['Missions could not be loaded. Please try reloading the page.']);
        }
    };
    const onImageUploadEnd = ({ result, image, onChange }) => {
        if (result === 'success' && image.path) {
            onChange(image.path);
        }
    };
    // callbacks
    const renderErrorMessages = useCallback(() => {
        if (pageStatus === 'error' || errorMessages.length > 0) {
            return errorMessages.map((message, index) => (React.createElement("p", { key: index, className: 'danger mt-2' }, message)));
        }
        return React.createElement(React.Fragment, null);
    }, [pageStatus, errorMessages]);
    const renderMissionList = useCallback(() => {
        if (pageStatus == 'loading') {
            return React.createElement(Spinner, null);
        }
        if (!missions) {
            return React.createElement(React.Fragment, null);
        }
        if (missions.length <= 0) {
            return React.createElement("p", null, "You have no missions!");
        }
        const defaultValue = getDefaultCheckedMissions(missions);
        return (React.createElement("div", { className: 'modern-card mt-3 mission-posting-to' },
            React.createElement("p", { className: 'small black border-bottom pb-2' }, "POSTING TO:"),
            React.createElement(Controller, { name: 'missionsToPost', control: control, defaultValue: defaultValue, render: ({ field: { onChange, value } }) => (React.createElement(React.Fragment, null, missions.map((mission) => (React.createElement(MissionItem, { key: mission.id, mission: mission, onChange: onChange, value: value }))))) })));
    }, [missions, pageStatus]);
    const renderSubmitButton = useCallback(() => {
        if (pageStatus === 'success') {
            return (React.createElement("button", { className: 'solid-bg-btn-primary w-100 mt-4', type: 'submit' }, "POST DISTANCE"));
        }
        return React.createElement(React.Fragment, null);
    }, [pageStatus]);
    // effects
    useEffect(() => {
        onUpdateMissions(exerciseType);
    }, [exerciseType]);
    return (React.createElement(React.Fragment, null,
        React.createElement("form", { onSubmit: handleSubmit(OnConfirmSubmit), className: 'post-distance' },
            React.createElement("h2", { className: 'primary-color' },
                "POST ",
                React.createElement("span", { className: 'secondary-color' }, "A DISTANCE")),
            React.createElement("p", { className: 'mb-4' },
                postDistanceData.totalTravelled,
                " ",
                postDistanceData.distanceUnit.toUpperCase(),
                ' ',
                "travelled so far on this mission!"),
            React.createElement("div", { className: 'd-flex align-items-center' },
                React.createElement("div", null,
                    React.createElement(MVMIcon, { icon: 'distance', height: 37.8, width: 52.5, className: 'primary-color' })),
                React.createElement("div", { className: 'input-container mx-3 w-60' },
                    React.createElement("input", { type: 'number', className: `form-control input-inset post-distance-input ${errors.travelled?.type && 'is-invalid'}`, step: '0.01', id: 'travelled', placeholder: '0.00', ...register('travelled', {
                            required: true,
                            maxLength: 45,
                            min: exerciseType === 'steps'
                                ? MIN_STEPS_TO_POST
                                : MIN_DISTANCE_TO_POST,
                        }) })),
                React.createElement("p", { className: 'text-capitalize weight-700 mb-0' }, isSteps() ? 'Steps' : postDistanceData.distanceUnit)),
            React.createElement("div", { className: 'danger text-center mt-1' }, errors.travelled?.type === 'required'
                ? 'Please add a distance'
                : errors.travelled?.type === 'min' && getMinDistanceMessage()),
            React.createElement("p", { className: 'tiny dark-grey ms-5 my-3' },
                React.createElement(MVMIcon, { height: 15.6, width: 16.9, icon: 'time-stats', className: 'dark-grey me-1' }),
                "Use the Activity Conversion Chart",
                ' ',
                React.createElement("a", { className: 'dark-grey', href: 'https://www.myvirtualmission.com/downloads/guides/mvm/MVM-Activity-Conversion-Chart-2021-01.pdf', target: '_blank' }, "HERE")),
            React.createElement("p", { className: 'small black mt-3 ms-2' }, "SELECT DATE & ACTIVITY:"),
            React.createElement("div", { className: 'd-flex' },
                React.createElement("div", { className: 'post-distance-card w-40' },
                    React.createElement(Controller, { name: 'startDate', control: control, defaultValue: todayFormatted, render: ({ field: { name, onChange, value } }) => (React.createElement(Datepicker, { updateDateValue: (name, newValue) => onChange(newValue), placeholder: 'Pick a date', direction: [endPickerDate, todayFormatted], value: value, name: name })) })),
                React.createElement("div", { className: 'post-distance-card ms-2 w-60' },
                    React.createElement("select", { id: 'exerciseType', className: 'form-select', defaultValue: postDistanceData.initialExercise, ...register('exerciseType', { required: true }) }, exerciseTypes.map((type) => (React.createElement("option", { key: type.alias + '_' + type.name, value: type.alias }, type.name)))))),
            React.createElement("p", { className: 'small black mt-4 ms-2' }, "COMMENT:"),
            React.createElement("textarea", { className: 'form-control', id: 'comment', defaultValue: '', placeholder: 'Add a comment...', rows: 3, ...register('comment') }),
            React.createElement("div", { className: 'mt-3' },
                React.createElement(Controller, { name: 'filePath', control: control, defaultValue: '', render: ({ field: { onChange } }) => (React.createElement(ImageUpload, { textContent: 'ADD PHOTO', onFinish: ({ ...params }) => onImageUploadEnd({ onChange, ...params }) })) })),
            renderMissionList(),
            renderErrorMessages(),
            renderSubmitButton()),
        React.createElement(PostDistanceModal, { onSubmit: handleSubmit(onSubmit), formData: modalData, missions: missions?.filter((mission) => modalData?.missionsToPost.includes(mission.id)) ||
                [], isPosting: isPosting, distanceUnit: isSteps() ? 'steps' : postDistanceData.distanceUnit })));
};
const rctPostDistance = document.getElementById('rct-post-distance');
if (rctPostDistance !== null) {
    ReactDOMClient.createRoot(rctPostDistance).render(React.createElement(PostDistance, null));
}
